import { Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Input from '@components/common/Input';
import MaskedInput from '@components/common/MaskedInput';
import { getCardIcon } from '@utils/cardHelper';
import { getCardType, validate } from '@utils/openPay';

import useStyles from './styles';

export interface GenericCardFieldsProps {
  errorFields?: boolean;
}

const GenericCardFields: FC<GenericCardFieldsProps> = ({ errorFields = false }) => {
  const { control, formState, watch } = useFormContext();
  const { t } = useTranslation();
  const css = useStyles();
  const cardProvider = getCardType(watch('cardNumber') || '');
  const CardProviderIcon = getCardIcon(cardProvider);

  return (
    <>
      <Grid className={css.formRow}>
        <Controller
          control={control}
          defaultValue=""
          name="holderName"
          rules={{ required: true }}
          render={({ field: { ref, ...field }, fieldState }) => (
            <Input
              {...field}
              inputRef={ref}
              label={t('checkoutScreen.payment.methods.card.name')}
              fullWidth
              inputProps={{
                'data-testid': 'holder-name',
              }}
              autoComplete="off"
              disabled={formState.isSubmitting}
              error={fieldState.invalid || errorFields}
            />
          )}
        />
      </Grid>
      <Grid className={css.formRow}>
        <Controller
          name="cardNumber"
          defaultValue=""
          rules={{ required: true, validate: validate.card }}
          control={control}
          render={({ field: { ref, ...field }, fieldState }) => (
            <MaskedInput
              {...field}
              inputRef={ref}
              label={t('checkoutScreen.payment.methods.card.number')}
              autoComplete="off"
              fullWidth
              inputProps={{
                inputMode: 'numeric',
                mask: '9999 9999 9999 9999',
                'data-testid': 'card-number',
              }}
              InputProps={{
                endAdornment: CardProviderIcon && <CardProviderIcon />,
              }}
              disabled={formState.isSubmitting}
              error={fieldState.invalid || errorFields}
            />
          )}
        />
      </Grid>
      <Grid container spacing={2}>
        <Grid xs={6} item>
          <Controller
            name="expiration"
            control={control}
            defaultValue=""
            rules={{ required: true, validate: validate.expiry }}
            render={({ field: { ref, ...field }, fieldState }) => (
              <MaskedInput
                {...field}
                inputRef={ref}
                label={t('checkoutScreen.payment.methods.card.validity')}
                placeholder={t('checkoutScreen.payment.methods.card.validityPlaceholder')}
                autoComplete="off"
                fullWidth
                inputProps={{
                  inputMode: 'numeric',
                  mask: '99/99',
                  'data-testid': 'expiration',
                }}
                disabled={formState.isSubmitting}
                error={fieldState.invalid || errorFields}
              />
            )}
          />
        </Grid>
        <Grid xs={6} item>
          <Controller
            name="cvv2"
            control={control}
            defaultValue=""
            rules={{ required: true, validate: (cvv) => validate.cvv(cvv, watch('cardNumber')) }}
            render={({ field: { ref, ...field }, fieldState }) => (
              <MaskedInput
                {...field}
                inputRef={ref}
                label={t('checkoutScreen.payment.methods.card.cvv')}
                autoComplete="off"
                fullWidth
                type="password"
                inputProps={{
                  'data-testid': 'cvv',
                  inputMode: 'numeric',
                  mask: '9999',
                }}
                disabled={formState.isSubmitting}
                error={fieldState.invalid || errorFields}
              />
            )}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default observer(GenericCardFields);
